/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { prepScopes } from '../utils/GraphHelpers';
/**
 * Group Type enumeration
 *
 * @export
 * @enum {number}
 */
export var GroupType;
(function (GroupType) {
    /**
     * Any group Type
     */
    GroupType[GroupType["any"] = 0] = "any";
    /**
     * Office 365 group
     */
    // tslint:disable-next-line:no-bitwise
    GroupType[GroupType["unified"] = 1] = "unified";
    /**
     * Security group
     */
    // tslint:disable-next-line:no-bitwise
    GroupType[GroupType["security"] = 2] = "security";
    /**
     * Mail Enabled Security group
     */
    // tslint:disable-next-line:no-bitwise
    GroupType[GroupType["mailenabledsecurity"] = 4] = "mailenabledsecurity";
    /**
     * Distribution Group
     */
    // tslint:disable-next-line:no-bitwise
    GroupType[GroupType["distribution"] = 8] = "distribution";
})(GroupType || (GroupType = {}));
/**
 * Searches the Graph for Groups
 *
 * @export
 * @param {IGraph} graph
 * @param {string} query - what to search for
 * @param {number} [top=10] - number of groups to return
 * @param {GroupType} [groupTypes=GroupType.any] - the type of group to search for
 * @returns {Promise<Group[]>} An array of Groups
 */
export function findGroups(graph, query, top = 10, groupTypes = GroupType.any) {
    return __awaiter(this, void 0, void 0, function* () {
        const scopes = 'Group.Read.All';
        let filterQuery = '';
        if (query !== '') {
            filterQuery = `(startswith(displayName,'${query}') or startswith(mailNickname,'${query}') or startswith(mail,'${query}'))`;
        }
        if (groupTypes !== GroupType.any) {
            const filterGroups = [];
            // tslint:disable-next-line:no-bitwise
            if (GroupType.unified === (groupTypes & GroupType.unified)) {
                filterGroups.push("groupTypes/any(c:c+eq+'Unified')");
            }
            // tslint:disable-next-line:no-bitwise
            if (GroupType.security === (groupTypes & GroupType.security)) {
                filterGroups.push('(mailEnabled eq false and securityEnabled eq true)');
            }
            // tslint:disable-next-line:no-bitwise
            if (GroupType.mailenabledsecurity === (groupTypes & GroupType.mailenabledsecurity)) {
                filterGroups.push('(mailEnabled eq true and securityEnabled eq true)');
            }
            // tslint:disable-next-line:no-bitwise
            if (GroupType.distribution === (groupTypes & GroupType.distribution)) {
                filterGroups.push('(mailEnabled eq true and securityEnabled eq false)');
            }
            filterQuery += (query !== '' ? ' and ' : '') + filterGroups.join(' or ');
        }
        const result = yield graph
            .api('groups')
            .filter(filterQuery)
            .top(top)
            .middlewareOptions(prepScopes(scopes))
            .get();
        return result ? result.value : null;
    });
}
